import React, { useRef } from 'react';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { ErrorNotification } from 'src/components/atoms/ErrorNotification/ErrorNotification';
import { BasketCTAButtonContainer } from 'src/components/molecules/BasketCTAButton/BasketCTAButtonContainer';
import { MAIN_PAGE_CTA_ID } from 'src/components/molecules/BasketCTAButton/elementIDs';
import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumbs/containers/BreadcrumbsContainer';
import { DeliveryEventContainer } from 'src/components/molecules/DeliveryEvent/containers/DeliveryEventContainer';
import { ExpandablePanel } from 'src/components/molecules/ExpandablePanel/ExpandablePanel';
import { InventoryStatusLabel } from 'src/components/molecules/InventoryStatusLabel/InventoryStatusLabel';
import { MembershipBannerSection } from 'src/components/molecules/MembershipBannerSection/MembershipBannerSection';
import { PartnerMaxiModuleWithTracking } from 'src/components/molecules/PartnerMaxiModule/components/PartnerMaxiModuleWithTracking';
import { PartnerMiniModule } from 'src/components/molecules/PartnerMiniModule/PartnerMiniModule';
import { ProductFeaturesWithTracking } from 'src/components/molecules/ProductFeatures/components/ProductFeaturesWithTracking';
import { getTopProductFeatures } from 'src/components/molecules/ProductFeatures/utils/getTopProductFeatures';
import { ProductPriceAndRatingWithTracking } from 'src/components/molecules/ProductPriceAndRating/ProductPriceAndRatingWithTracking';
import { ProductRatingMaxiSummary } from 'src/components/molecules/ProductRatingMaxiSummary/ProductRatingMaxiSummary';
import { StickySection } from 'src/components/molecules/StickySection/components/StickySection';
import { TrustCarouselWithTracking } from 'src/components/molecules/TrustCarousel/components/TrustCarouselWithTracking';
import { DeliverySectionContainer } from 'src/components/organisms/DeliverySection/containers/DeliverySectionContainer/DeliverySectionContainer';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel/containers/LinkCarouselContainer';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription/containers/ProductDescriptionContainer';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel';
import { ProductOptionsContainer } from 'src/components/organisms/ProductOptions/ProductOptionsContainer';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews/containers/ProductReviewsContainer';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf/containers/RecommendedProductsShelfContainer';
import { Placement } from 'src/constants/recommendations';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { isOutOfStock, isSuspended } from 'src/utils/inventoryStatus';
import { useEditProductPersonalisationOptions } from '../hooks/useEditProductPersonalisationOptions';
import { useProductsRecommendations } from '../hooks/useProductsRecommendations';
import {
  DELIVERY_HEADING,
  getOptionsHeadingText,
  NEW_PRODUCT_LABEL,
  PRODUCT_DETAILS_HEADING,
} from './copy';
import { PARTNER_MODULE_ID } from './elementIds';
import { useStandardLayout } from './useStandardLayout';
import * as styles from './StandardLayout.styles';

interface StandardLayoutProps {
  averageProductRating?: number;
}

export const StandardLayout = ({ averageProductRating }: StandardLayoutProps) => {
  const {
    basketMutationError,
    openContactPartnerModal,
    optionsCostRange,
    pageLiveRegionText,
    partnerHref,
    product,
  } = useStandardLayout();
  const formRef = useRef<HTMLFormElement>(null);
  const optionsHeading = getOptionsHeadingText(
    product.purchasable,
    product.options.length > 0,
    product.specialFeatures.personalisable,
  );
  const suspended = isSuspended(product.availability, product.available);

  const { placements } = useProductsRecommendations({
    categoryId: product.breadcrumbCategoryId,
    productCode: product.code,
    productTitle: product.title,
    placements: [
      Placement.SimilarProducts,
      Placement.SameSeller2,
      Placement.ActiveProducts,
      Placement.RecentlyViewed,
    ],
  });

  const { allowsGiftWrap, options, productRating, specialFeatures } = product;
  const personalisationAddsCost =
    options.length > 0 && specialFeatures.personalisable && optionsCostRange.minRequired > 0;
  const minimumPersonalisationCost = personalisationAddsCost ? optionsCostRange.minRequired : 0;
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;
  const productFeatures = getTopProductFeatures({
    allowsGiftWrap,
    productRating,
    specialFeatures,
  });

  useEditProductPersonalisationOptions();

  return (
    <>
      <div css={visuallyHidden} role="status">
        {pageLiveRegionText}
      </div>
      <div css={styles.wrapper}>
        <div css={[maxWidthContainer, styles.grid, styles.breadcrumbsContainer]}>
          <div css={styles.fullWidthColumn}>
            <BreadcrumbContainer />
          </div>
        </div>

        {/* start grid containing a column that spans across rows */}
        <div css={[maxWidthContainer, styles.grid, styles.largeSpacingBottom]}>
          <h1 css={[styles.narrowColumn, styles.productTitle]}>{product.title}</h1>
          <div css={styles.narrowColumn}>
            <PartnerMiniModule
              linkHref={`#${PARTNER_MODULE_ID}`}
              location={product.partner.location}
              logoSrc={product.partner.logo}
              name={product.partner.name}
              partnerRating={averageProductRating}
            />
          </div>
          <div css={[styles.widerColumn, styles.spanAcrossRows]}>
            <ProductImagesCarouselContainer />
          </div>
          <div css={styles.narrowColumn}>
            <ProductPriceAndRatingWithTracking
              averageRating={product.productRating || undefined}
              numberOfReviews={product.numberOfReviews}
              preSalePrices={product.preSalePrices}
              price={product.price}
              priceIsMinimum={priceIsMinimum}
              reviewsLinkHref={`#${REVIEWS_URL_FRAGMENT_ID}`}
              salePercentage={product.salePercentage || 0}
            />
            <InventoryStatusLabel inventoryStatus={product.inventoryStatus} suspended={suspended} />
            <DeliveryEventContainer />
            {product.isNew && <p css={visuallyHidden}>{NEW_PRODUCT_LABEL}</p>}
          </div>
          {productFeatures.length > 0 && (
            <div css={styles.narrowColumn}>
              <ProductFeaturesWithTracking
                currency={product.price.currency}
                minimumPersonalisationCost={minimumPersonalisationCost}
                productFeatures={productFeatures}
              />
            </div>
          )}
          <div css={styles.narrowColumn}>
            <ErrorNotification returnFocusToRef={formRef} shouldShow={basketMutationError} />
            {optionsHeading && <h2 css={styles.sectionHeading}>{optionsHeading}</h2>}
            <ProductOptionsContainer ref={formRef} />
            <BasketCTAButtonContainer />
          </div>
          {!isOutOfStock(product.inventoryStatus) && (
            <div css={styles.narrowColumn}>
              <h2 css={styles.sectionHeading}>{DELIVERY_HEADING}</h2>
              <DeliverySectionContainer />
            </div>
          )}
        </div>
        {/* end grid containing a column that spans across rows */}

        <div css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}>
          <div css={styles.fullWidthColumn}>
            <ExpandablePanel
              collapseButtonText="Read less"
              expandButtonText="Read more"
              onToggle={(expanded) =>
                sendGAEvent({
                  event: 'custom_event',
                  event_category: PRODUCT_DETAILS_HEADING,
                  event_action: expanded ? 'Expand' : 'Collapse',
                  event_label: 'not set',
                })
              }
            >
              <h2 css={styles.heading}>{PRODUCT_DETAILS_HEADING}</h2>
              <ProductDescriptionContainer />
            </ExpandablePanel>
          </div>
        </div>

        <div
          css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}
          id={REVIEWS_URL_FRAGMENT_ID}
        >
          <div css={styles.fullWidthColumn}>
            <div css={styles.overallRatingWrapper}>
              <ProductRatingMaxiSummary
                averageRating={product.productRating}
                numberOfReviews={product.numberOfReviews}
              />
            </div>
            <div css={styles.productReviewsWrapper}>
              <ProductReviewsContainer />
            </div>
          </div>
        </div>

        <div css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}>
          <div css={styles.fullWidthColumn}>
            <RecommendedProductsShelfContainer
              lazyImages
              placementType={Placement.SimilarProducts}
              products={placements[Placement.SimilarProducts]?.products}
              title={placements[Placement.SimilarProducts]?.placementTitle}
            />
          </div>
        </div>

        <div css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}>
          <div css={styles.fullWidthColumn}>
            <TrustCarouselWithTracking />
          </div>
        </div>

        <div css={[styles.partnerModules]} id={PARTNER_MODULE_ID}>
          <div css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}>
            <div css={styles.fullWidthColumn}>
              <PartnerMaxiModuleWithTracking
                location={product.partner.location}
                logoSrc={product.partner.logo}
                name={product.partner.name}
                onContactPartnerClick={openContactPartnerModal}
                partnerLinkHref={partnerHref}
                partnerRating={averageProductRating}
                partnerShortcode={product.partner.shortcode}
              />
            </div>
            <div css={[styles.fullWidthColumn, styles.largeSpacingBottom]}>
              <RecommendedProductsShelfContainer
                lazyImages
                placementType={Placement.SameSeller2}
                products={placements[Placement.SameSeller2]?.products}
                title={placements[Placement.SameSeller2]?.placementTitle}
              />
            </div>
          </div>
        </div>

        <div css={[maxWidthContainer, styles.customGrid, styles.largeSpacingBottom]}>
          <div css={styles.fullWidthColumn}>
            <RecommendedProductsShelfContainer
              lazyImages
              placementType={Placement.ActiveProducts}
              products={placements[Placement.ActiveProducts]?.products}
              title={placements[Placement.ActiveProducts]?.placementTitle}
            />
          </div>
          <div css={styles.fullWidthColumn}>
            <MembershipBannerSection />
          </div>
          <div css={styles.fullWidthColumn}>
            <LinkCarouselContainer />
          </div>
          <div css={styles.fullWidthColumn}>
            <RecommendedProductsShelfContainer
              lazyImages
              placementType={Placement.RecentlyViewed}
              products={placements[Placement.RecentlyViewed]?.products}
              title={placements[Placement.RecentlyViewed]?.placementTitle}
            />
          </div>
        </div>
        <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
          <BasketCTAButtonContainer />
        </StickySection>
      </div>
    </>
  );
};
